import dayjs from 'dayjs';
import { useState } from 'react';

import { useFilteredPostsNavigation } from '../../filtered-posts/hook/use-filtered-posts-navigation';
import { useFilteredProfilesNavigation } from '../../filtered-profiles/hook/use-filtered-profiles-navigation';
import { usePerformance } from '../../shared/hooks';
import { calculateDateRangeFromBar } from '../helpers/calculate-date-range-from-bar';
import { useAddFilterToAskSyncly } from '../hooks';
import { useSelectedPeriod } from '../hooks';
import { ChartPeriodSelector } from './chart-period-selector';

import { platformColorPalette, platformOptions } from '@/entities/public-filter/public-filter-constants';
import { ChartLoader } from '@/shared/components/chart-loader';
import type { ChartDataPoint } from '@/shared/components/charts/stack-chart-types';
import { SectionHeader } from '@/shared/components/section-header';
import { Card, CardContent, CardHeader } from '@/shared/components/shadcn/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import { useSharedDateRange } from '@/shared/hooks/use-shared-date-range';
import { chartPeriodToDayjsUnit } from '@/shared/utils/date/chart-period-to-dayjs-unit';
import { StackBarChart } from '../../../../shared/components/charts/stack-chart';
import { usePublicFilter } from '../../shared/public-filter/use-public-filter';
import { useTrendStackChart } from '../hooks';
import { showDrillDownContextMenu } from '../../shared/overlay/show-drill-down-context-menu';

type Metric = 'count' | 'view';

export function TrendsSection({ fetchEnabled }: { fetchEnabled: boolean }) {
  const { query: selectedQuery } = useSelectedSavedQuery();
  const { dateRange } = useSharedDateRange();
  const [selectedMetric, setSelectedMetric] = useState<Metric>('count');
  const [selectedPeriod, setSelectedPeriod] = useSelectedPeriod({ dateRange });
  const { publicFilter } = usePublicFilter(selectedQuery.id);
  const navigateToFilteredPosts = useFilteredPostsNavigation();
  const navigateToFilteredProfiles = useFilteredProfilesNavigation();
  const addFilterToAskSyncly = useAddFilterToAskSyncly();

  const { data, isFetching } = usePerformance({
    period: selectedPeriod,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    queryId: selectedQuery.id,
    enabled: fetchEnabled,
    publicFilter,
  });

  const { chartData, chartConfig } = useTrendStackChart({
    trends:
      data?.trends.sort((a, b) => {
        const aOrder = platformOptions.find((o) => o.value === a.platform)?.order || 0;
        const bOrder = platformOptions.find((o) => o.value === b.platform)?.order || 0;
        return bOrder - aOrder;
      }) || [],
    selectedMetric: selectedMetric as 'count' | 'view',
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    dateUnit: chartPeriodToDayjsUnit(selectedPeriod),
    colorPalette: platformColorPalette,
  });

  const handleBarClick = async (dataPoint: ChartDataPoint, event: React.MouseEvent) => {
    const action = await showDrillDownContextMenu({
      position: { x: event.clientX, y: event.clientY },
      menus: ['view-posts', 'view-profiles', 'ask-syncly'],
    });

    if (action === 'close') return;

    const { startDate, endDate } = calculateDateRangeFromBar(dataPoint.date, selectedPeriod);

    // Format dates for URL
    const startDateStr = dayjs(startDate).format('YYYY-MM-DD');
    const endDateStr = dayjs(endDate).format('YYYY-MM-DD');

    switch (action) {
      case 'view-posts':
        await navigateToFilteredPosts({
          filter: {},
          dimension: 'default', // Use default dimension for date-based filtering without specific filters
          dateRange: {
            startDate: startDateStr,
            endDate: endDateStr,
          },
        });
        break;
      case 'view-profiles':
        await navigateToFilteredProfiles({
          filter: {},
          dimension: 'default', // Use default dimension for date-based filtering without specific filters
          dateRange: {
            startDate: startDateStr,
            endDate: endDateStr,
          },
        });
        break;
      case 'ask-syncly':
        addFilterToAskSyncly({
          dateRange: {
            startDate: startDateStr,
            endDate: endDateStr,
          },
          filter: null,
        });
        break;
    }
  };

  return (
    <div>
      <SectionHeader title="Trends" description="Keep track of changes in your performance" />
      <div className="p-6">
        <Card className="border border-zinc-200 bg-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <Select
                value={selectedMetric}
                onValueChange={(value) => setSelectedMetric(value as Metric)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="count">Total volume</SelectItem>
                  <SelectItem value="view">Views</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-3">
                <ChartPeriodSelector value={selectedPeriod} onChange={setSelectedPeriod} />
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <div className="relative h-100">
              <StackBarChart
                chartData={chartData}
                chartConfig={chartConfig}
                dateUnit={chartPeriodToDayjsUnit(selectedPeriod)}
                onBarClick={handleBarClick}
              />
              {isFetching && <ChartLoader className="absolute" />}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
